import { FabricText, Rect, Circle, Triangle, Group } from 'fabric';
import EventPublisher from '../event_publisher';
import {
  POSITION_SIZE,
  POSITION_FILL_COLOR,
  POSITION_FILL_COLOR_LIGHT,
  POSITION_BOOKED_COLOR,
  POSITION_STROKE_WIDTH,
  POSITION_TARGET_COLOR,
  POSITION_TARGET_STROKE_WIDTH,
  POSITION_DIMMED_OPACITY,
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  CELL_SIZE,
  TOP_PADDING,
  LEFT_PADDING
} from './render_properties';

export default class PostionRenderer {
  constructor(position, readOnly = false) {
    this.position = position;
    this.readOnly = readOnly;
    this.topPadding = readOnly ? TOP_PADDING : 0;
    this.leftPadding = readOnly ? LEFT_PADDING : 0;
    let positionFillColor = readOnly ? POSITION_FILL_COLOR_LIGHT : POSITION_FILL_COLOR;
    this.fill = position.booked ? POSITION_BOOKED_COLOR : positionFillColor;
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    return this;
  }

  render() {
    const label = this.renderLabel();
    this.shape = this.renderShape();
    this.renderedElement = this.groupElements(label, this.shape);
  }

  reRender() {
    this.remove();
    this.render();
    this.canvas.setActiveObject(this.renderedElement);
    this.canvas.fire('object:modified');
  }

  remove() {
    this.canvas.remove(this.renderedElement);
  }

  groupElements(label, shape) {
    const group = new Group([label, shape], {
      padding: 10,
      width: CELL_SIZE,
      height: CELL_SIZE,
      left: (this.position.x * CELL_SIZE) + this.leftPadding,
      top: (this.position.y * CELL_SIZE) + this.topPadding,
      originX: 'left',
      originY: 'top',
      lockMovementX: this.readOnly, lockMovementY: this.readOnly
    });
    group.on('selected', this.onSelected);
    group.on('moving', this.onMoving);
    group.on('modified', this.onModified);
    group.on('selection:moved', this.onSelectionMoved);
    group.on('selection:created', this.onSelectionCreated);
    group.hasControls = false;
    this.canvas.add(group);
    return group;
  }

  onModified = event => {
    if (event.action === 'drag') {
      this.onMoved(event)
    }
  }

  onSelected = () => {
    EventPublisher.publish('position-selected', { position: this.position });
  }

  onSelectionCreated = () => {
    this.initialX = this.position.x;
    this.initialY = this.position.y;
  }

  onSelectionMoved = event => {
    const { leftOffset, topOffset } = event;
    this.position.x = (leftOffset / CELL_SIZE) + this.initialX;
    this.position.y = (topOffset / CELL_SIZE) + this.initialY;
    EventPublisher.publish('object-moved', { object: this.position });
  }

  onMoving = () => {
    const { left, top } = this.renderedElement;
    this.position.x = left / CELL_SIZE;
    this.position.y = top / CELL_SIZE;
    EventPublisher.publish('object-moving', { object: this.position });
  }

  onMoved = () => {
    EventPublisher.publish('object-moved', { object: this.position });
  }

  renderLabel() {
    const text = new FabricText(this.position.identifier, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontWeight: FONT_WEIGHT,
      selectable: false,
      originX: 'center',
      originY: 'center',
      top: -35
    });
    return text;
  }

  renderSquare() {
    return new Rect(this.getPositionDefaultArgs());
  }

  renderCircle() {
    return new Circle({
      ...this.getPositionDefaultArgs(),
      radius: POSITION_SIZE / 2,
    });
  }

  renderTriangle() {
    return new Triangle(
      this.getPositionDefaultArgs(POSITION_SIZE, Math.sqrt(3) * POSITION_SIZE / 2)
    );
  }

  renderDiamond() {
    const width = POSITION_SIZE / Math.sqrt(2);
    const height = POSITION_SIZE / Math.sqrt(2);
    return new Rect({
      ...this.getPositionDefaultArgs(width, height),
      angle: 45,
      centeredRotation: true,
    });
  }

  getPositionDefaultArgs(width = POSITION_SIZE, height = POSITION_SIZE) {
    return {
      width: width,
      height: height,
      selectable: false,
      stroke: this.position.highlight,
      strokeWidth: POSITION_STROKE_WIDTH,
      fill: this.fill,
      originX: 'center',
      originY: 'center',
    }
  }

  renderShape() {
    const renderers = {
      square: this.renderSquare(),
      triangle: this.renderTriangle(),
      diamond: this.renderDiamond(),
    };
    return renderers[this.position.shape] || this.renderCircle();
  }

  setActive() {
    this.canvas.setActiveObject(this.renderedElement);
  }

  setFillColor(fill) {
    this.fill = fill;
    this.shape.set({ fill: fill });
    this.canvas.requestRenderAll();
  }

  // Picker mode: 'target' lights the outline, 'chosen' fills it, 'dimmed' fades the group,
  // anything else restores
  setMoveState(state) {
    const target = state === 'target' || state === 'chosen';
    this.shape.set({
      stroke: target ? POSITION_TARGET_COLOR : this.position.highlight,
      strokeWidth: target ? POSITION_TARGET_STROKE_WIDTH : POSITION_STROKE_WIDTH,
      fill: state === 'chosen' ? POSITION_TARGET_COLOR : this.fill
    });
    this.renderedElement.set({ opacity: state === 'dimmed' ? POSITION_DIMMED_OPACITY : 1 });
    this.renderedElement.hoverCursor = this.moveCursor(state);
  }

  moveCursor(state) {
    if (state === 'target' || state === 'chosen') return 'pointer';
    if (state === 'dimmed') return 'default';
    return null;
  }
}
